import styled from '@emotion/styled';
import { uiTheme as t } from './theme';
import FieldError from './FieldError';
import { controlStyles } from './TextField';

export interface TextAreaProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  maxLength: number;
  rows?: number;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  'aria-describedby'?: string;
}

const Wrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
});

const Area = styled.textarea<{ invalid: boolean }>(({ invalid }) => ({
  ...controlStyles(invalid),
  minHeight: '6.5rem',
  resize: 'vertical',
  lineHeight: 1.65,
  padding: '0.8125rem 0.875rem',
}));

const Counter = styled.div<{ 'data-over': boolean }>(props => ({
  alignSelf: 'flex-end',
  fontFamily: t.font,
  fontSize: '0.75rem',
  marginTop: '0.375rem',
  color: props['data-over'] ? t.color.danger : t.color.muted,
}));

/**
 * Campo multilínea con contador informativo.
 *
 * El contador avisa cuando el valor supera el máximo, pero no bloquea la
 * escritura para dejar la decisión final a la validación de la pantalla.
 */
const TextArea = ({
  id,
  value,
  onChange,
  maxLength,
  rows = 4,
  placeholder,
  error,
  disabled,
  ...rest
}: TextAreaProps) => {
  const describedBy =
    [rest['aria-describedby'], error ? `${id}-error` : undefined].filter(Boolean).join(' ') || undefined;

  return (
    <Wrapper>
      <Area
        id={id}
        value={value}
        rows={rows}
        placeholder={placeholder}
        disabled={disabled}
        invalid={!!error}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        onChange={event => onChange(event.target.value)}
      />
      <Counter data-over={value.length > maxLength}>{`${value.length}/${maxLength}`}</Counter>
      {error && <FieldError id={`${id}-error`}>{error}</FieldError>}
    </Wrapper>
  );
};

export default TextArea;
